import { Field, TextAreaEditor, TextEditor } from '@approvalmax/ui';
import { constants } from 'modules/common';
import { FC, memo } from 'react';
import bemFactory from 'react-bem-factory';

import RequestFormRow from '../../../../../components/RequestFormRow/RequestFormRow';
import RequestFormRowGroup from '../../../../../components/RequestFormRowGroup/RequestFormRowGroup';
import RequestFormSection from '../../../../../components/RequestFormSection/RequestFormSection';
import { useQBOVendorRequestContext } from '../../QBOVendorRequestContext';
import { messages } from './AddressSection.messages';

const { qBooksConstants } = constants;

const qa = bemFactory.qa('reqf-qbooks-vendor-request__address');

interface AddressSectionProps {
    className?: string;
}

const AddressSection: FC<AddressSectionProps> = (props) => {
    const { className } = props;

    const { changeVendorDeepProperty, vendor, showErrors } = useQBOVendorRequestContext();

    const isInvalidStreetAddress = !!(
        vendor.address.street &&
        (!vendor.address.street?.trim() || vendor.address.street.length > qBooksConstants.ADDRESS_STREET_MAX_LENGTH)
    );

    const isInvalidRegion = !!(
        vendor.address.region &&
        (!vendor.address.region?.trim() || vendor.address.region.length > qBooksConstants.ADDRESS_REGION_MAX_LENGTH)
    );

    const isInvalidPostCode = !!(
        vendor.address.postCode &&
        (!vendor.address.postCode?.trim() ||
            vendor.address.postCode.length > qBooksConstants.ADDRESS_POSTCODE_MAX_LENGTH)
    );

    const isInvalidCountry = !!(
        vendor.address.country &&
        (!vendor.address.country?.trim() || vendor.address.country.length > qBooksConstants.ADDRESS_COUNTRY_MAX_LENGTH)
    );

    const isInvalidCity = !!(
        vendor.address.city &&
        (!vendor.address.city?.trim() || vendor.address.city.length > qBooksConstants.ADDRESS_CITY_MAX_LENGTH)
    );

    return (
        <RequestFormSection header={messages.addressHeader} className={className}>
            <RequestFormRowGroup>
                <RequestFormRow>
                    <Field title={messages.street}>
                        <TextAreaEditor
                            qa={qa(`address-address-editor`)}
                            maxLength={qBooksConstants.ADDRESS_STREET_MAX_LENGTH}
                            value={vendor.address.street || ''}
                            onChange={changeVendorDeepProperty('address', 'street')}
                            placeholder={messages.streetAddressPlaceholder}
                            invalid={showErrors && isInvalidStreetAddress}
                            minHeight={4}
                            maxHeight={8}
                        />
                    </Field>

                    <div>
                        <RequestFormRow>
                            <Field title={messages.cityTown}>
                                <TextEditor
                                    qa={qa('city-town')}
                                    maxLength={qBooksConstants.ADDRESS_CITY_MAX_LENGTH}
                                    value={vendor.address.city || ''}
                                    onChange={changeVendorDeepProperty('address', 'city')}
                                    placeholder={messages.cityTownPlaceholder}
                                    invalid={showErrors && isInvalidCity}
                                />
                            </Field>

                            <Field title={messages.countyStateProvince}>
                                <TextEditor
                                    qa={qa('county-state-province')}
                                    maxLength={qBooksConstants.ADDRESS_REGION_MAX_LENGTH}
                                    value={vendor.address.region || ''}
                                    onChange={changeVendorDeepProperty('address', 'region')}
                                    placeholder={messages.countyStateProvincePlaceholder}
                                    invalid={showErrors && isInvalidRegion}
                                />
                            </Field>
                        </RequestFormRow>

                        <RequestFormRow>
                            <Field title={messages.postalCode}>
                                <TextEditor
                                    qa={qa('postal-code')}
                                    maxLength={qBooksConstants.ADDRESS_POSTCODE_MAX_LENGTH}
                                    value={vendor.address.postCode || ''}
                                    onChange={changeVendorDeepProperty('address', 'postCode')}
                                    placeholder={messages.postalCodePlaceholder}
                                    invalid={showErrors && isInvalidPostCode}
                                />
                            </Field>

                            <Field title={messages.country}>
                                <TextEditor
                                    qa={qa('country')}
                                    maxLength={qBooksConstants.ADDRESS_COUNTRY_MAX_LENGTH}
                                    value={vendor.address.country || ''}
                                    onChange={changeVendorDeepProperty('address', 'country')}
                                    placeholder={messages.countyPlaceholder}
                                    invalid={showErrors && isInvalidCountry}
                                />
                            </Field>
                        </RequestFormRow>
                    </div>
                </RequestFormRow>
            </RequestFormRowGroup>
        </RequestFormSection>
    );
};

export default memo(AddressSection);
